<template>
  <Transition name="offline-slide">
    <div
      v-if="showOffline || showReconnected"
      class="fixed inset-x-0 top-0 z-[9998] shadow-lg"
      :class="showReconnected ? 'bg-emerald-700/95' : 'bg-gray-800/95'"
      style="padding-top: env(safe-area-inset-top, 0px)"
      role="status"
      aria-live="polite">
      <div class="flex gap-2.5 justify-center items-center px-4 py-2.5">
        <fa-icon
          :icon="showReconnected ? 'wifi' : 'wifi-slash'"
          class="text-xs"
          :class="showReconnected ? 'text-emerald-200' : 'text-gray-300'" />
        <span class="text-sm font-medium text-white">
          {{ showReconnected ? 'Back online' : "You're offline" }}
        </span>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, watch, onUnmounted } from 'vue'
import { useNetworkStatus } from '@/use/useNetworkStatus'

// Delay before showing the offline state — iOS briefly reports no connection
// on resume, and short blips shouldn't flash a banner.
const OFFLINE_SHOW_DELAY_MS = 1500
const RECONNECTED_VISIBLE_MS = 2000

const { isOnline } = useNetworkStatus()

const showOffline = ref(false)
const showReconnected = ref(false)
let offlineTimer = null
let reconnectedTimer = null

watch(
  isOnline,
  (online) => {
    clearTimeout(offlineTimer)
    clearTimeout(reconnectedTimer)

    if (online) {
      if (showOffline.value) {
        showOffline.value = false
        showReconnected.value = true
        reconnectedTimer = setTimeout(() => {
          showReconnected.value = false
        }, RECONNECTED_VISIBLE_MS)
      }
    } else {
      showReconnected.value = false
      offlineTimer = setTimeout(() => {
        showOffline.value = true
      }, OFFLINE_SHOW_DELAY_MS)
    }
  },
  { immediate: true }
)

onUnmounted(() => {
  clearTimeout(offlineTimer)
  clearTimeout(reconnectedTimer)
})
</script>

<style scoped>
.offline-slide-enter-active,
.offline-slide-leave-active {
  transition: all 0.3s ease;
}

.offline-slide-enter-from,
.offline-slide-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
</style>
